import React from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import Switcher from "../components/switcher";
import { initWebpay } from "../services/api";

const formatDate = (date) => {
    if(!date) return '';
    return new Date(date).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
}

const formatPrice = (amount, currency = 'CLP') => {
    const options = {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'CLP' ? 0 : 2,
        maximumFractionDigits: currency === 'CLP' ? 0 : 2,
    };
    return amount.toLocaleString(currency === 'CLP' ? 'es-CL' : 'en-US', options);
}

export default function Checkout(){
    const { state } = useLocation();
    const navigate = useNavigate();
    const hasData = Boolean(state && state.total);
    const [form, setForm] = React.useState({
        nombre: '',
        apellido: '',
        documento: '',
        direccion: '',
        email: '',
        telefono: '',
    });
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    // El % de anticipo es la fuente de verdad del backend (viene del hold/departure).
    // TODO confirmar con negocio: default 50% vs 100%
    const depositPercent = state?.depositPercent;
    const depositAmount = state?.deposit ?? (state?.total && depositPercent != null ? state.total * (depositPercent / 100) : 0);
    const isHoldExpired = state?.holdExpiresAt ? new Date(state.holdExpiresAt).getTime() < Date.now() : false;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev)=>({...prev, [name]: value}));
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if(!hasData){
            setError('No hay reserva para procesar.');
            return;
        }
        const missing = Object.entries(form).filter(([,v])=>!v);
        if(missing.length){
            setError('Completa todos los campos.');
            const firstMissing = document.getElementById(missing[0][0]);
            if(firstMissing) firstMissing.focus();
            return;
        }
        setError('');
        setLoading(true);
        if(isHoldExpired){
            setError('El cupo expiró, vuelve a seleccionar la salida.');
            setLoading(false);
            return;
        }
        // amount_cents es solo informativo: el backend recalcula el monto server-side
        // (precio + extras + % anticipo) e ignora este valor por seguridad.
        const amountBase = depositAmount || state?.total || 0;
        const amountCents = Math.round(Number(amountBase));
        const payload = {
            package_id: state?.packageId || state?.id || null,
            departure_id: state?.departureId || null,
            hold_id: state?.holdId || null,
            quantity: state?.totalPeople || state?.adults || 1,
            amount_cents: amountCents,
            currency: state?.currency || 'CLP',
            buyer_name: `${form.nombre} ${form.apellido}`.trim(),
            buyer_email: form.email,
            extras_ids: state?.extrasIds || [],
            return_url: `${window.location.origin}/payment-return`,
        };
        initWebpay(payload)
            .then((resp)=>{
                window.location.href = `${resp.url}?token_ws=${resp.token}`;
            })
            .catch((err)=>{
                setError(err.message || 'No se pudo iniciar el pago.');
                setLoading(false);
            });
    }

    return (
        <>
            <Navbar navclass="defaultscroll is-sticky" navlight={true} manuclass="justify-end nav-light"/>
            <section className="relative table w-full items-center py-24 bg-[url('../images/bg/cta.jpg')] bg-top bg-no-repeat bg-cover">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>
                <div className="container relative">
                    <div className="grid grid-cols-1 pb-8 text-center mt-10">
                        <h3 className="text-3xl leading-normal tracking-wider font-semibold text-white">Pago</h3>
                        <p className="text-white/70 mt-2">Completa tus datos y confirma el anticipo.</p>
                    </div>
                </div>
            </section>

            <section className="relative md:py-16 py-12">
                <div className="container relative">
                    {!hasData && (
                        <div className="text-center bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-8">
                            <p className="text-lg font-semibold mb-3">No hay reserva cargada.</p>
                            <Link to="/grid" className="text-red-500 font-semibold hover:underline">Explorar tours</Link>
                        </div>
                    )}

                    {hasData && (
                        <div className="grid md:grid-cols-3 grid-cols-1 gap-6">
                            <div className="md:col-span-2 bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-6">
                                <h5 className="text-xl font-semibold mb-4">Tus datos</h5>
                                <form className="space-y-4" onSubmit={handleSubmit}>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="nombre" className="font-semibold block mb-1">Nombre</label>
                                            <input id="nombre" name="nombre" type="text" autoComplete="given-name" value={form.nombre} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                        <div>
                                            <label htmlFor="apellido" className="font-semibold block mb-1">Apellido</label>
                                            <input id="apellido" name="apellido" type="text" autoComplete="family-name" value={form.apellido} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="documento" className="font-semibold block mb-1">RUT o Pasaporte</label>
                                            <input id="documento" name="documento" type="text" inputMode="text" autoComplete="off" value={form.documento} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="font-semibold block mb-1">Email</label>
                                            <input id="email" type="email" name="email" autoComplete="email" value={form.email} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="telefono" className="font-semibold block mb-1">Teléfono</label>
                                            <input id="telefono" name="telefono" type="tel" inputMode="tel" autoComplete="tel" value={form.telefono} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                        <div>
                                            <label htmlFor="direccion" className="font-semibold block mb-1">Dirección de origen</label>
                                            <input id="direccion" name="direccion" type="text" autoComplete="street-address" value={form.direccion} onChange={handleChange} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent dark:bg-slate-900" />
                                        </div>
                                    </div>
                                    <p className="text-red-500 text-sm empty:hidden" role="alert" aria-live="polite">{error}</p>
                                    <button type="submit" className={`w-full py-2 px-4 rounded-md ${loading ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} text-white`} disabled={loading}>
                                        {loading ? 'Redirigiendo…' : 'Confirmar pago'}
                                    </button>
                                </form>
                            </div>

                            <div className="bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-6">
                                <h5 className="text-xl font-semibold mb-4">Resumen</h5>
                                <div className="space-y-2 text-sm">
                                    {state.destination && (
                                        <p><span className="font-semibold">Destino:</span> {state.destination}</p>
                                    )}
                                    <p><span className="font-semibold">Fechas:</span> {formatDate(state.startDate)} - {formatDate(state.endDate)}</p>
                                    <p><span className="font-semibold">Personas:</span> {state.adults} adultos {state.children > 0 ? `, ${state.children} niños` : ''}</p>
                                    <p><span className="font-semibold">Extras:</span> {state.extras && state.extras.length ? state.extras.join(', ') : 'Sin extras'}</p>
                                    <div className="flex justify-between pt-2">
                                        <span>Total</span>
                                        <span className="font-semibold">{formatPrice(state.total, state.currency)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Anticipo ({depositPercent}%)</span>
                                        <span className="font-semibold text-red-500">{formatPrice(depositAmount, state.currency)}</span>
                                    </div>
                                    {state.holdExpiresAt && (
                                        <p className={`text-xs ${isHoldExpired ? 'text-red-500' : 'text-slate-500'}`}>
                                            Cupo reservado hasta {new Date(state.holdExpiresAt).toLocaleTimeString('es-CL')}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-4">
                                    <Link to="/cart" className="text-red-500 font-semibold hover:underline text-sm">Volver al carrito</Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer/>
            <Switcher/>
        </>
    )
}
